import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { HelpCircle } from "lucide-react";
import { getClientiAgenzia } from "@/api/agenzia";
import type { ClienteAffiliato } from "@/types";
import { ErrorState } from "@/components/ErrorState";
import { OnboardingTour } from "@/onboarding/OnboardingTour";
import { clientiListSteps, clientiListVersion } from "@/onboarding/clientiListSteps";
import { useOnboarding } from "@/onboarding/useOnboarding";
import { useAuth } from "@/auth/useAuth";
import { useListView } from "@/hooks/useListView";
import { ListViewToggle } from "@/components/ListViewToggle";

// Righe mock mostrate SOLO durante il tour onboarding su pagina vuota: gli
// step "apri cliente" / "paginazione" puntano a elementi che altrimenti
// mancherebbero. Non sono clienti reali, click disabilitato.
const MOCK_CLIENTI: ClienteAffiliato[] = [
  {
    id: "__mock_c1__",
    first_name: "Mario",
    last_name: "Rossi (esempio)",
    email: "mario.rossi@example.it",
    phone: "+39 333 0000000",
    opportunities_count: 2,
  },
  {
    id: "__mock_c2__",
    first_name: "Lucia",
    last_name: "Bianchi (esempio)",
    email: "lucia.bianchi@example.it",
    phone: "+39 347 0000000",
    opportunities_count: 1,
  },
];

function Initials({ name }: { name: string }) {
  const parts = name.split(" ").filter(Boolean);
  const letters = ((parts[0]?.[0] ?? "") + (parts[1]?.[0] ?? "")).toUpperCase();
  return (
    <div className="w-8 h-8 rounded-full bg-brand/10 text-brand text-xs font-semibold flex items-center justify-center flex-shrink-0">
      {letters || "?"}
    </div>
  );
}

const PAGE_SIZE = 20;

export function ClientiAgenzia() {
  const navigate = useNavigate();
  const [view, setView] = useListView("clienti");
  const [items, setItems] = useState<ClienteAffiliato[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState("");
  const [pendingSearch, setPendingSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [replayNonce, setReplayNonce] = useState(0);
  const [replayActive, setReplayActive] = useState(false);
  const [tourJustFinished, setTourJustFinished] = useState(false);
  const { seenVersion } = useOnboarding("clienti_list");
  const tourSeen = seenVersion >= clientiListVersion;
  const { hasRole } = useAuth();
  const isStaff = hasRole("affiliato") || hasRole("operatore");
  const tourIsActive = (!tourSeen && !tourJustFinished) || replayActive;
  const showMock = tourIsActive && !loading && !error && items.length === 0;
  const rowsToRender = showMock ? MOCK_CLIENTI : items;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    getClientiAgenzia({
      limit: PAGE_SIZE,
      offset: page * PAGE_SIZE,
      q: search || undefined,
    })
      .then((res) => {
        if (cancelled) return;
        setItems(res.items);
        setTotal(res.total);
      })
      .catch((e) => {
        console.error(e);
        if (!cancelled) setError("Impossibile caricare i clienti.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [page, search]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  function submitSearch(e: React.FormEvent) {
    e.preventDefault();
    setSearch(pendingSearch.trim());
    setPage(0);
  }

  return (
    <div className="space-y-4">
      {/* Montato a elenco caricato: i passi si agganciano alle righe, che prima
          del caricamento non ci sono ancora. */}
      {!loading && (
        <OnboardingTour
          steps={clientiListSteps}
          flagKey="clienti_list"
          version={clientiListVersion}
          replayNonce={replayNonce}
          onClose={() => {
            setTourJustFinished(true);
            setReplayActive(false);
          }}
        />
      )}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <h2 className="text-lg font-semibold text-text">Clienti</h2>
          {isStaff && (
            <button
              type="button"
              onClick={() => {
                setTourJustFinished(false);
                setReplayActive(true);
                setReplayNonce((n) => n + 1);
              }}
              className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-text transition-colors"
              title="Rivedi guida"
            >
              <HelpCircle size={14} />
              <span>Rivedi guida</span>
            </button>
          )}
        </div>
        <div className="flex items-center gap-2">
          <ListViewToggle view={view} onChange={setView} />
          <form onSubmit={submitSearch} className="flex gap-2 flex-1" data-tour="clienti-search">
            <input
              type="text"
              value={pendingSearch}
              onChange={(e) => setPendingSearch(e.target.value)}
              placeholder="Cerca..."
              className="flex-1 px-3 py-2 border border-border rounded-lg text-sm bg-card min-w-0"
            />
            <button
              type="submit"
              className="px-3 py-2 bg-brand text-white rounded-lg text-sm flex-shrink-0"
            >
              Cerca
            </button>
          </form>
        </div>
      </div>

      {error ? (
        <ErrorState message={error} />
      ) : loading ? (
        <p className="text-sm text-muted-foreground">Caricamento...</p>
      ) : rowsToRender.length === 0 ? (
        <p className="text-sm text-muted-foreground">Nessun cliente.</p>
      ) : (
        <div className={view === "card" ? "" : "bg-card rounded-lg border border-border overflow-hidden"} data-tour="clienti-table">
          {showMock && (
            <div className="px-3 py-2 text-xs text-muted-foreground bg-amber-50 border-b border-amber-200">
              Righe di esempio — clienti fittizi, servono solo per il tour guidato.
            </div>
          )}
          {/* Mobile: card list */}
          <div className={view === "card" ? "grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3" : "sm:hidden divide-y divide-border"}>
            {rowsToRender.map((c) => {
              const name = c.nome_visualizzato || [c.first_name, c.last_name].filter(Boolean).join(" ") || "—";
              const isMock = showMock;
              return (
                <div
                  key={c.id}
                  onClick={isMock ? undefined : () => navigate(`/agenzia/clienti/${c.id}`)}
                  role={isMock ? undefined : "button"}
                  tabIndex={isMock ? undefined : 0}
                  onKeyDown={
                    isMock
                      ? undefined
                      : (e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            navigate(`/agenzia/clienti/${c.id}`);
                          }
                        }
                  }
                  className={(view === "card" ? "bg-card border border-border rounded-xl " : "") + (isMock ? "p-3 opacity-70" : "p-3 cursor-pointer hover:bg-surface transition-colors")}
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-text">{name}</div>
                      {c.email && <div className="text-xs text-muted-foreground truncate mt-0.5">{c.email}</div>}
                      {c.phone && <div className="text-xs text-muted-foreground mt-0.5">{c.phone}</div>}
                    </div>
                    <span className="text-xs font-semibold text-brand bg-brand/10 px-2 py-1 rounded-full flex-shrink-0">
                      {c.opportunities_count} contratti
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
          {/* Desktop: table */}
          <table className={view === "card" ? "hidden" : "hidden sm:table w-full text-sm"}>
            <thead className="bg-surface text-muted-foreground">
              <tr>
                <th className="text-left py-2 px-3 font-medium">Nome</th>
                <th className="text-left py-2 px-3 font-medium">Email</th>
                <th className="text-left py-2 px-3 font-medium">Telefono</th>
                <th className="text-right py-2 px-3 font-medium">N° contratti</th>
              </tr>
            </thead>
            <tbody>
              {rowsToRender.map((c) => {
                const name = c.nome_visualizzato || [c.first_name, c.last_name].filter(Boolean).join(" ") || "—";
                const isMock = showMock;
                return (
                  <tr
                    key={c.id}
                    onClick={isMock ? undefined : () => navigate(`/agenzia/clienti/${c.id}`)}
                    role={isMock ? undefined : "button"}
                    tabIndex={isMock ? undefined : 0}
                    onKeyDown={
                      isMock
                        ? undefined
                        : (e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              navigate(`/agenzia/clienti/${c.id}`);
                            }
                          }
                    }
                    className={
                      isMock
                        ? "border-t border-border opacity-70"
                        : "border-t border-border cursor-pointer hover:bg-surface transition-colors"
                    }
                  >
                    <td className="py-2 px-3">
                      <div className="flex items-center gap-3">
                        <Initials name={name} />
                        <span className="text-text">{name}</span>
                      </div>
                    </td>
                    <td className="py-2 px-3 text-text">{c.email ?? "—"}</td>
                    <td className="py-2 px-3 text-text">{c.phone ?? "—"}</td>
                    <td className="py-2 px-3 text-right text-text">{c.opportunities_count}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <div className="flex items-center justify-between text-sm" data-tour="clienti-pagination">
        <span className="text-muted-foreground">
          Pagina {page + 1} di {totalPages} — {total} totali
        </span>
        <div className="flex gap-2">
          <button
            disabled={page === 0 || loading}
            onClick={() => setPage((p) => Math.max(0, p - 1))}
            className="px-3 py-1 border border-border rounded disabled:opacity-50"
          >
            Precedente
          </button>
          <button
            disabled={page + 1 >= totalPages || loading}
            onClick={() => setPage((p) => p + 1)}
            className="px-3 py-1 border border-border rounded disabled:opacity-50"
          >
            Successiva
          </button>
        </div>
      </div>
    </div>
  );
}
